/**
 * NovoCompromissoDialog — SlideOverPanel para agendar (ou remarcar) um
 * compromisso no lead.
 *
 * Campos: titulo*, data/hora*, duracao, tipo, local, responsavel, observacoes.
 * Sem `compromisso`, submete via `onCriar(dados)`; com `compromisso` (modo
 * edicao, CRM-25), pre-preenche e submete `onSalvar(id, campos)` com o PATCH
 * — antes remarcar a visita so dava excluindo e recriando. O responsavel vem
 * de `useResponsaveisCrm` e comeca no responsavel do lead (CRM-22/25): tudo
 * nascia sem dono e nao chegava ao sino de ninguem. Erros do backend
 * tratados inline (silentError no AsyncButton do footer).
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import useResponsaveisCrm from "../../../hooks/useResponsaveisCrm";
import { TIPOS_COMPROMISSO } from "../../../services/crm";
import {
  Field,
  FormStack,
  InlineError,
  InlineHint,
  Input,
  Select,
  Textarea,
} from "../styles";

import { paraDatetimeLocal } from "./datetimeLocal";

const DURACAO_PADRAO_MIN = 60;

function valoresDe(compromisso, responsavelPadrao) {
  return {
    titulo: compromisso?.titulo || "",
    dataHora: paraDatetimeLocal(compromisso?.data_hora),
    duracao: compromisso?.duracao_minutos || DURACAO_PADRAO_MIN,
    tipo: compromisso?.tipo || "REUNIAO",
    local: compromisso?.local || "",
    observacoes: compromisso?.observacoes || "",
    responsavel: compromisso ? compromisso.responsavel || "" : responsavelPadrao || "",
  };
}

function NovoCompromissoDialog({
  open,
  onClose,
  onCriar,
  onSalvar,
  compromisso = null,
  responsavelPadrao = "",
  subtitle,
}) {
  const editando = Boolean(compromisso);
  const { responsaveis } = useResponsaveisCrm({ enabled: open });
  const [valores, setValores] = useState(() => valoresDe(compromisso, responsavelPadrao));
  const [erro, setErro] = useState(null);

  // Cada abertura parte do compromisso a editar (ou do formulario vazio).
  useEffect(() => {
    if (open) {
      setValores(valoresDe(compromisso, responsavelPadrao));
      setErro(null);
    }
  }, [open, compromisso, responsavelPadrao]);

  const setCampo = (campo, valor) => setValores((prev) => ({ ...prev, [campo]: valor }));

  const fechar = () => {
    setErro(null);
    onClose();
  };

  const submeter = async () => {
    setErro(null);
    if (!valores.titulo.trim()) {
      setErro("Informe um título.");
      throw new Error("Título obrigatório.");
    }
    if (!valores.dataHora) {
      setErro("Informe a data e hora.");
      throw new Error("Data/hora obrigatoria.");
    }
    const dados = {
      titulo: valores.titulo.trim(),
      // datetime-local -> ISO (sem timezone; backend interpreta como local).
      data_hora: valores.dataHora,
      duracao_minutos: Number(valores.duracao) || DURACAO_PADRAO_MIN,
      tipo: valores.tipo,
      local: valores.local.trim(),
      observacoes: valores.observacoes.trim(),
      // Chave sempre presente: vazio = sem responsavel (o backend limpa).
      responsavel: valores.responsavel || null,
    };
    if (editando) {
      await onSalvar(compromisso.id, dados);
    } else {
      await onCriar(dados);
    }
    onClose();
  };

  return (
    <SlideOverPanel
      open={open}
      onClose={fechar}
      title={editando ? "Remarcar compromisso" : "Novo compromisso"}
      subtitle={subtitle}
      width="min(95vw, 480px)"
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={fechar}>
            Cancelar
          </button>
          <AsyncButton
            onClick={submeter}
            successMessage={editando ? "Compromisso atualizado." : "Compromisso agendado."}
            silentError
          >
            {editando ? "Salvar" : "Agendar"}
          </AsyncButton>
        </>
      }
    >
      <FormStack>
        <Field>
          Título *
          <Input
            type="text"
            value={valores.titulo}
            onChange={(e) => setCampo("titulo", e.target.value)}
            placeholder="Ex: Visita técnica para medir"
            maxLength={200}
            autoFocus
          />
        </Field>
        <Field>
          Data e hora *
          <Input
            type="datetime-local"
            value={valores.dataHora}
            onChange={(e) => setCampo("dataHora", e.target.value)}
          />
        </Field>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Field style={{ flex: 1, minWidth: 120 }}>
            Tipo
            <Select value={valores.tipo} onChange={(e) => setCampo("tipo", e.target.value)}>
              {TIPOS_COMPROMISSO.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field style={{ flex: 1, minWidth: 120 }}>
            Duração (min)
            <Input
              type="number"
              min={5}
              step={5}
              value={valores.duracao}
              onChange={(e) => setCampo("duracao", e.target.value)}
            />
          </Field>
        </div>
        <Field>
          Responsável
          <Select
            aria-label="Responsável"
            value={valores.responsavel}
            onChange={(e) => setCampo("responsavel", e.target.value)}
          >
            <option value="">— O responsável do lead —</option>
            {responsaveis.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </Select>
          <InlineHint>Quem recebe o compromisso no sino e na Agenda.</InlineHint>
        </Field>
        <Field>
          Local
          <Input
            type="text"
            value={valores.local}
            onChange={(e) => setCampo("local", e.target.value)}
            placeholder="Ex: Showroom / endereço do cliente"
            maxLength={200}
          />
        </Field>
        <Field>
          Observacoes
          <Textarea
            value={valores.observacoes}
            onChange={(e) => setCampo("observacoes", e.target.value)}
            rows={2}
          />
        </Field>
        <InlineHint>O compromisso entra na agenda do lead e na timeline.</InlineHint>
        {erro && <InlineError>{erro}</InlineError>}
      </FormStack>
    </SlideOverPanel>
  );
}

export default NovoCompromissoDialog;
